import EventLog, { EventLogEvent } from '@tv2/lib-core/logging/eventLogger';
import { NormalizedError, NormalizedErrorType } from '../types';
import type { ErrorEvent as ErrorEventType } from '../types/chromecast-caf-receiver/cast.framework.events';
import endpoints from '../config/endpoints';
import { name } from '../../package.json';
import { version } from '../../release.json';
import store, { USER_INFORMATION } from '../store';
import cast from '../types/chromecast-caf-receiver';
import { ERROR_TYPES } from './errorMessages';

const logger = new EventLog({
  logUrl: endpoints.clientsideLogger,
  appName: name,
  appVersion: version,
  // FIXME:
  // @ts-expect-error: We can't use the userAgent for EventLogs
  userAgent: navigator.userAgent,
});

interface IErrorHandler {
  canHandle(error: any): boolean;
  handle(error: any): NormalizedError[];
}

const { DetailedErrorCode } = cast.framework.events || {};

const ERROR_MESSAGE = { INVALID_JWT: 'Invalid JWT', UNKNOWN_ERROR: 'Unknown error' };
type GraphQLError = {
  message: string;
  extensions?: {
    code: string;
    errorType: string;
    exception: string;
  };
};

type GraphQLErrorEvent = {
  errors?: GraphQLError[];
  responseJson?: { errors: GraphQLError[] };
  response?: any;
  data?: { media?: null | { streams?: null | any } };
};

type PluginError = {
  name: string;
  type: string;
  error: { message: string; stack: string };
  params: any;
};

type PossibleErrors =
  | PromiseRejectionEvent
  | ErrorEvent
  | GraphQLErrorEvent
  | Error
  | Event
  | PluginError;

export type NormalizedErrorWithFatal = NormalizedError & { isErrorTypeFatal: boolean };

/**
 * Simple function to determine whether an error is fatal.
 * @param  {NormalizedErrorType} type Error type, used for determining overlay.
 * @return {boolean}
 */
function isErrorTypeFatal(type?: NormalizedErrorType): boolean {
  if (!type) return false;

  const fatalErrors = [
    ERROR_TYPES.UNAUTHENTICATED,
    ERROR_TYPES.INVALID_AUTH_TOKEN,
    ERROR_TYPES.UNHANDLE_REJECTION,
    ERROR_TYPES.LOAD_FAILED,
    ERROR_TYPES.REFRESH_TOKEN,
    ERROR_TYPES.HTTP_ERROR,
    ERROR_TYPES.LICENSE_NOT_GRANTED,
    ERROR_TYPES.GEO_LOCATION_BLOCKED,
    ERROR_TYPES.CONCURRENCY_LIMIT_VIOLATION,
    ERROR_TYPES.DOES_NOT_EXISTS,
    ERROR_TYPES.FORMAT_NOT_AVAILABLE,
    ERROR_TYPES.NO_DOWN_LOADURL,
    ERROR_TYPES.NO_FILES,
    ERROR_TYPES.NO_PUBLIC_URLS,
    ERROR_TYPES.NO_STREAMING_URL,
    ERROR_TYPES.NOT_APPROVED,
    ERROR_TYPES.NOT_AVAILABLE_RESTRICTION,
    ERROR_TYPES.NOT_AVAILABLE,
    ERROR_TYPES.NOT_YET_AVAILABLE,
    ERROR_TYPES.NOT_YET_AVAILABLE_RESTRICTION,
    ERROR_TYPES.EXPIRED,
    ERROR_TYPES.EXPIRED_RESTRICTION,
    ERROR_TYPES.SCRIPT_LOAD_ERROR,
    ERROR_TYPES.DOWNSTREAM_SERVICE_ERROR,
    ERROR_TYPES.GRAPHQL_VALIDATION_FAILED,
    ERROR_TYPES.DEVICE_NOT_GRANTED,
    ERROR_TYPES.SUBTITLE_ERROR,
    ERROR_TYPES.DUPLICATE_LIVE_STREAM_VIOLATION,
  ];

  return fatalErrors.some(fatalError => fatalError.toLowerCase() === type.toLowerCase());
}

function useMessageAsType(message: string) {
  if (message === ERROR_MESSAGE.INVALID_JWT) return ERROR_TYPES.UNAUTHENTICATED;

  return ERROR_TYPES.GRAPHQL_GENERIC_ERROR;
}

/**
 * Converts the enum number to a detailed text.
 * @param {ErrorEventType['detailedErrorCode']} errorCode Enum number of the errorCode.
 * @returns {string}
 */
function getDetailedError(errorCode: ErrorEventType['detailedErrorCode']): string {
  let detailedError = 'GENERIC';
  if (errorCode) {
    const matchingErrors = Object.entries(DetailedErrorCode).find(value =>
      value.includes(errorCode)
    );
    if (matchingErrors) {
      // eslint-disable-next-line prefer-destructuring
      detailedError = matchingErrors[0];
    }
  }
  return detailedError;
}

/**
 * Find the error from a shaka error event.
 * @param {Error | Error[]} error An object or array of errors from shaka.
 * @returns {Error | undefined}
 */
function getStackFromShakaError(error: Error | Error[]): Error | undefined {
  if (Array.isArray(error)) {
    return error.find(e => e instanceof Error);
  }
  return undefined;
}

/**
 * Unified error object.
 * @param {NormalizedErrorType} type Type of error.
 * @param {string} message Message of the error.
 * @param {Error | string} stack Stack trace of the error.
 * @param {any} raw The raw error information.
 * @returns {NormalizedError}
 */
function LogError(
  type: NormalizedErrorType,
  message: string,
  stack?: Error | string,
  raw?: any
): NormalizedError {
  return {
    type,
    message,
    stack,
    raw,
  };
}

/**
 * Handles error event from the CAF SDK.
 * @param {ErrorEventType} error Cast error event.
 * @returns {NormalizedError}
 */
class CastErrorEventHandler implements IErrorHandler {
  handle(error: ErrorEventType): NormalizedError[] {
    const { detailedErrorCode } = error;

    // LOAD failed always happens after an error. Logging this only gives noise.
    if (detailedErrorCode === DetailedErrorCode.LOAD_FAILED) return [];

    let type = error?.error?.type || error?.type;

    // Problem with parsing subtitles
    if (error?.error.shakaErrorCode === 2005) {
      type = ERROR_TYPES.SUBTITLE_ERROR;
    }

    const stack = getStackFromShakaError(error?.error?.shakaErrorData);
    return [LogError(type, getDetailedError(detailedErrorCode), stack, error)];
  }

  canHandle(error: any): boolean {
    return error instanceof cast.framework.events.ErrorEvent;
  }
}

/**
 * Handles the error response from the graphQL call.
 * @param {object} error Error object from graphQL response.
 * @returns {NormalizedError}
 */
class GraphQLHandler implements IErrorHandler {
  handle(error: GraphQLErrorEvent): NormalizedError[] {
    const { errors, responseJson } = error;

    const mergedErrors = [...(errors ?? []), ...(responseJson?.errors ?? [])];

    return mergedErrors.map(mergedError => {
      const { message, extensions } = mergedError;
      const { errorType, code } = extensions ?? {};

      const type = errorType ?? code ?? useMessageAsType(message);

      return LogError(type, message, undefined, extensions);
    });
  }

  canHandle(error: any): boolean {
    return (
      error?.data?.media === null ||
      error?.data?.media?.streams === null ||
      error?.response !== undefined
    );
  }
}

/**
 * Handles generic Error object or Event.
 * @param {Error|Event} error Event or Error.
 * @returns {NormalizedError}
 */
class ErrorHandler implements IErrorHandler {
  handle(error: Error): NormalizedError[] {
    const { message, stack } = error;
    return [LogError('Error', message, stack)];
  }

  canHandle(error: any): boolean {
    return error instanceof Error && (error as any).response === undefined;
  }
}

/**
 * Handles errors from plugins.
 * @param {object} err Error emitted from plugins.
 * @returns {NormalizedError}
 */
class PluginErrorHandler implements IErrorHandler {
  handle(error: PluginError): NormalizedError[] {
    const {
      name: pluginName,
      type,
      error: { message, stack },
      params,
    } = error;

    return [LogError(type, `Error in ${pluginName}`, stack, { message, stack, params })];
  }

  canHandle(error: any): boolean {
    return error.type === 'plugin.error';
  }
}

/**
 * Handles errors emitted from the global promise rejected event.
 * @param {PromiseRejectionEvent} error ErrorEvent from promise.
 * @returns {NormalizedError}
 */
class PromiseRejectionEventHandler implements IErrorHandler {
  handle(error: PromiseRejectionEvent): NormalizedError[] {
    const { message, stack } = error?.reason ?? {};
    return [LogError(error.type, message, stack)];
  }

  canHandle(error: any): boolean {
    return error instanceof PromiseRejectionEvent;
  }
}

class UnknownError implements IErrorHandler {
  handle(error: any): NormalizedError[] {
    return [LogError(ERROR_TYPES.UNKNOWN, ERROR_MESSAGE.UNKNOWN_ERROR, undefined, error)];
  }

  canHandle(error: any): boolean {
    return true;
  }
}

/**
 * Process the error.
 * @param {PossibleErrors} error Error object.
 * @returns {NormalizedError[]} Array of the normalized errors.
 */
function processErrors(error: PossibleErrors): NormalizedError[] {
  const handlers: IErrorHandler[] = [
    new CastErrorEventHandler(),
    new GraphQLHandler(),
    new ErrorHandler(),
    new PluginErrorHandler(),
    new PromiseRejectionEventHandler(),
    new UnknownError(),
  ];
  let returnedError = [] as NormalizedError[];

  for (let i: number = 0; i < handlers.length; i += 1) {
    if (handlers[i].canHandle(error)) {
      returnedError = [...returnedError, ...handlers[i].handle(error)];
      break;
    }
  }

  return returnedError;
}

/**
 * Normalizing the error for a context the fits chromecast errors.
 * @param  {ErrorEvent} err The error.
 * @return {NormalizedErrorWithFatal[]} Array of the normalized errors.
 */
export function normalizeErrors(err: any): NormalizedErrorWithFatal[] {
  const errors = processErrors(err);
  return errors.map(e => ({
    ...e,
    isErrorTypeFatal: isErrorTypeFatal(e.type),
  }));
}

/**
 * Pushing error to the eventLogger.
 * @param  {Error} error The error.
 */
export function log(error: EventLogEvent['error']) {
  const event = {
    user: store.get(USER_INFORMATION),
    location: window.location,
    error,
    context: `chromecast/${error.type}`,
  };

  logger.error(event);
}
export function getLogger() {
  return logger;
}
